import React, { useMemo, useState } from 'react';
import styles from './SongFromSpotifyForm.module.css';
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as yup from 'yup';
import Button from '../../../../ui/Buttons/Button/Button';
import FormField from '../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../ui/DataEntry/Label/Label';
import { StylesConfig, ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../App.types';
import Select from '../../../../ui/DataEntry/Select/Select';
import { getSongs, usePaginatedFetch } from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import debounce from 'lodash/debounce';
import { DropdownOptionWithSubLabel, OptionWithSubLabel } from '@round/ui-kit';

export type SongFromSpotifyFormProps = {
    handleSubmit: (values: SongFromSpotifyFormValues, helpers: FormikHelpers<SongFromSpotifyFormValues>) => void;
    initialValues?: SongFromSpotifyFormValues;
    renderActions?: (props: FormikProps<SongFromSpotifyFormValues>) => React.ReactNode;
    disabledFields?: (keyof SongFromSpotifyFormValues)[];
};

type SongFromSpotifyFormValues = {
    url: string;
    song: ValueType<GenericDropdownOption<number>, false>;
};

const defaultInitialValues: SongFromSpotifyFormValues = {
    url: '',
    song: null,
};

const validationSchema = yup.object({
    url: yup.string().required().url('Enter valid url'),
});

const defaultRenderActions: SongFromSpotifyFormProps['renderActions'] = ({ submitForm, isSubmitting, isValid }) => (
    <Button
        className={styles.submitButton}
        type="filled"
        color="black"
        onClick={submitForm}
        loading={isSubmitting}
        disabled={!isValid}
    >
        Submit
    </Button>
);

// const created to keep the same instance of an array, because it is used
// in the dependency list. If passed as default value with array literal
// it would end up on infinite update.
const defaultDisabledFields: SongFromSpotifyFormProps['disabledFields'] = [];

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#c2cfe0',
    }),
    valueContainer: (base, state) => ({
        ...base,
        fontSize: '0.8125rem',
        padding: state.isMulti ? '0.625rem 1rem 0.4375rem 1rem' : '0.625rem 1rem',
    }),
    multiValue: (base) => ({
        ...base,
        margin: '0 3px 3px 0',
    }),
    multiValueRemove: (base) => ({
        ...base,
        height: '1rem',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#90a0b7',
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 1000,
    }),
};

const SongFromSpotifyForm = ({
    handleSubmit,
    initialValues = defaultInitialValues,
    renderActions = defaultRenderActions,
    disabledFields = defaultDisabledFields,
}: SongFromSpotifyFormProps) => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    const {
        data: songs,
        loading: songsLoading,
        getCurrentPage,
        hasNextPage: hasSongsNextPage,
    } = usePaginatedFetch(getSongs, { page, search, page_size: 10 });
    const getSongsCurrentPage = useMemo(() => debounce(getCurrentPage, 700), [getCurrentPage]);

    useAbortableEffect(
        (signal) => {
            if (Array.isArray(disabledFields) && !disabledFields?.includes('song')) {
                getSongsCurrentPage({ signal });
            }
        },
        [getSongsCurrentPage, disabledFields]
    );

    const songOptions: DropdownOptionWithSubLabel<number>[] = useMemo(
        () => songs.map((s) => ({ value: s.id, label: s.title, subLabel: s.artists.map((a) => a.name).join(', ') })),
        [songs]
    );

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {(props) => (
                <>
                    <Form>
                        <FormField>
                            <Label htmlFor="url">Spotify URL</Label>
                            <Field id="url" name="url" placeholder="Enter Spotify URL" />
                            <ErrorMessage name="url">
                                {(msg) => <span className={styles.error}>{msg}</span>}
                            </ErrorMessage>
                        </FormField>
                        <FormField>
                            <Label htmlFor="song">Song</Label>
                            <Field name="song">
                                {(props: FieldProps) => (
                                    <Select
                                        styles={selectStyles}
                                        isLoading={songsLoading}
                                        isDisabled={Boolean(
                                            disabledFields?.includes(
                                                props.field.name as keyof SongFromSpotifyFormValues
                                            )
                                        )}
                                        inputValue={search}
                                        onInputChange={(value) => {
                                            setSearch(value);
                                            setPage(1);
                                        }}
                                        options={songOptions}
                                        placeholder="Select song to link"
                                        value={props.field.value}
                                        onChange={(value) => props.form.setFieldValue(props.field.name, value)}
                                        onMenuScrollToBottom={() => {
                                            if (hasSongsNextPage) {
                                                setPage((page) => page + 1);
                                            }
                                        }}
                                        components={{ Option: OptionWithSubLabel }}
                                    />
                                )}
                            </Field>
                        </FormField>
                    </Form>
                    {renderActions(props)}
                </>
            )}
        </Formik>
    );
};

export default SongFromSpotifyForm;
